import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Stack,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
  DialogTitle,
  DialogActions,
  Tabs,
  Tab,
  Menu,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { beginProfile } from "../../utils/renderProfiler";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadIcon from "@mui/icons-material/UploadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import GroupWorkOutlinedIcon from "@mui/icons-material/GroupWorkOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PlaylistAddCheckCircleOutlinedIcon from "@mui/icons-material/PlaylistAddCheckCircleOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query/react";

import RegisterDeviceModal from "../admin/RegisterDeviceModal";
import InstallationCertificateModal from "../admin/InstallationCertificateModal";
import ReportsPortal from "../reports/ReportsPortal";
import SensorAssignModal from "./SensorAssignModal";
import { useStatus } from "../../popup/NotificationContext";
import {
  useBulkDeleteDevicesMutation,
  useDeleteDeviceMutation,
  useGetAssignableUsersQuery,
  useGetDeviceExpiringPageV2Query,
  useGetDeviceRenewalsPageV2Query,
  useGetDevicesPageQuery,
  useEditDeviceMutation,
  useGetDeviceNotificationTemplateQuery,
  usePermanentlyDeleteDeviceMutation,
  useRevealUserPasswordMutation,
  useRestoreDeviceMutation,
} from "../../redux/admin/adminApi";
import { useAddDevicesToGroupMutation, useGetAllGroupsQuery } from "../../redux/admin/groupApi";
import { useGetFleetSummaryQuery, useLazyGetDeviceV2ByIdQuery } from "../../redux/admin/deviceApi";
import { useGetAllUsersQuery } from "../../redux/admin/dashboardApi";
import { downloadEntityTemplate, uploadEntityFile } from "../../services/importExportService";
import {
  useCreateNotificationSettingMutation,
  useUpdateNotificationSettingMutation,
} from "../../redux/admin/notificationApi";
import { getDeviceActionsEffective, normalizeRole, ROLES } from "../../utils/rbacHelper";
import {
  CUSTOM_ROWS_PER_PAGE_OPTIONS,
  getDisplayRange,
  getRowsPerPageLabel,
  getTotalPagesForRows,
  ROWS_PER_PAGE_MENU_PROPS,
} from "../../utils/tablePagination";
import { getDeviceStatus as resolveVehicleStatus } from "../../utils/vehicleStatus";
import { mergeLegacyDeviceDetail } from "../../utils/deviceV2Adapter";
import { readDeviceViewCache, writeDeviceViewCache } from "../../utils/deviceViewCache";
import { useDevRenderInstrumentation } from "../../utils/devInstrumentation";

const finishDeviceListPortalModalProfile = beginProfile("DeviceListPortalModal", { stage: "component" });

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const FILTER_OPTIONS = [
  { value: "imei", label: "IMEI" },
  { value: "vehicle", label: "Vehicle no" },
  { value: "sim", label: "Sim" },
  { value: "status", label: "Status" },
  { value: "model", label: "Model" },
  { value: "vehicleType", label: "Vehicle type" },
  { value: "group", label: "Group" },
  { value: "customer", label: "Customer" },
  { value: "dealer", label: "Dealer" },
  { value: "deleted", label: "Deleted Device" },
  { value: "contact", label: "Contact" },
  { value: "creationDate", label: "Creation Date" },
];

const DEVICE_LIST_MODAL_PAGE_CACHE_SCOPE = "device-list-modal-page-v2";

const STATUS_FILTER_OPTIONS = [
  { value: "running", label: "Running" },
  { value: "idle", label: "Idle" },
  { value: "stopped", label: "Stopped" },
  { value: "not reporting", label: "Not Reporting", searchText: "inactive offline no reporting no active" },
  { value: "no data", label: "No Data" },
  { value: "expired", label: "Expired" },
];

const EXTEND_VALIDITY_OPTIONS = [1, 2, 3, 4, 5, 6];

const PRIMARY_TABS = [
  { value: "devices", label: "Devices List" },
  { value: "renewal", label: "Renewal Report" },
  { value: "expiring", label: "Expiring Devices" },
  { value: "deleted", label: "Deleted Devices" },
];

const START_OF_DAY = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const END_OF_DAY = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const normalizeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value < 1e12 ? value * 1000 : value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
    }
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDeviceRecencyTs = (device) =>
  Math.max(
    toTimestampMs(device?.profileUpdatedAt),
    toTimestampMs(device?.createdAt)
  );

const formatDate = (value, withTime = false) => {
  const date = normalizeDate(value);
  if (!date) return "-";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(withTime
      ? {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }
      : {}),
  });
};

const numberValue = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const addYears = (value, years) => {
  const base = normalizeDate(value) || new Date();
  const next = new Date(base);
  next.setMonth(next.getMonth() + Number(years || 0) * 12);
  return next;
};

const getLicenseInfo = (device) => {
  const license = device?.deviceLicense || {};
  const runtime = device?.licenseRuntime || {};
  const expiryDate = runtime?.expiryDate || license?.expiryDate || null;
  const expiresAt = normalizeDate(expiryDate);
  const now = new Date();
  const diffDays = expiresAt ? Math.ceil((START_OF_DAY(expiresAt) - START_OF_DAY(now)) / 86400000) : null;
  const type = license?.licenseType || device?.licenseType || "Yearly";
  return {
    expiryDate,
    expiresAt,
    diffDays,
    type: String(type).replace(/_/g, " "),
  };
};

const getDeviceStatus = (device) => {
  const rawStatus = String(resolveVehicleStatus(device) || "no_data");
  
  // Normalize status for consistent filtering
  const normalized = rawStatus.toLowerCase().replace(/_/g, " ").trim();
  
  // Map specific statuses to standard values
  if (normalized.includes("expired")) return "expired";
  if (normalized.includes("not reporting") || normalized.includes("offline")) return "not reporting";
  if (normalized.includes("inactive")) return "inactive";
  if (normalized.includes("unknown")) return "no data";
  if (normalized.includes("no data") || normalized === "no_data" || normalized === "nodata") return "no data";
  if (normalized.includes("running")) return "running";
  if (normalized.includes("stop")) return "stopped";
  if (normalized.includes("idle") || normalized.includes("idling")) return "idle";
  
  return normalized;
};

const getDisplayDeviceStatus = (device, licenseInfo = getLicenseInfo(device)) => {
  const rawStatus = normalizeStatusBucket(getDeviceStatus(device));

  if (licenseInfo?.diffDays != null && licenseInfo.diffDays < 0) return "Expired";
  if (rawStatus === "expired") return "Expired";

  if (rawStatus === "inactive") {
    return "No Reporting";
  }

  if (["unknown", "no data", "no_data", "nodata"].includes(rawStatus)) {
    return "No Data";
  }

  if (["running", "moving"].includes(rawStatus)) {
    return "Running";
  }

  if (["idle", "idling", "ignition on", "acc on", "engine on", "engine start", "started"].includes(rawStatus)) {
    return "Idle";
  }

  if (["stopped", "parked", "ignition off", "acc off", "engine off", "engine stop"].includes(rawStatus)) {
    return "Stopped";
  }

  return "No Data";
};

const getStatusColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("running")) return "#10b981";
  if (value.includes("stop")) return "#dc2626";
  if (value.includes("idle")) return "#f59e0b";
  if (value.includes("inactive") || value.includes("no reporting")) return "#0ea5e9";
  if (value.includes("no data")) return "#6b7280";
  if (value.includes("expired")) return "#ef4444";
  return "#f43f5e";
};

const extractRemarks = (device) => {
  const customField = (Array.isArray(device?.customFields) ? device.customFields : []).find(
    (field) => String(field?.key || "").toLowerCase() === "remarks" || String(field?.fieldName || "").toLowerCase() === "remarks"
  );
  return customField?.fieldValue || device?.remarks || "-";
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

const displayName = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return value;
  return value?.username || value?.name || value?.email || value?.uniqueId || value?.deviceId || "-";
};

const resolveCreatedByLabel = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return String(value).trim() || "-";
  const username = String(value?.username || value?.name || "").trim();
  const uniqueId = String(value?.uniqueId || value?.userId || value?.deviceId || "").trim();
  if (username && uniqueId) return `${username} (${uniqueId})`;
  return username || uniqueId || String(value?.email || "").trim() || "-";
};

const getEntityId = (value) => String(value?._id || value?.id || value || "").trim();

const getUniqueEntityEntriesWithDirectory = (value, directoryMap) => {
  const uniqueEntries = new Map();

  toArray(value).forEach((entry) => {
    if (!entry) return;
    const id = getEntityId(entry);
    const directoryEntry = id ? directoryMap?.get(id) : null;
    const candidate = entry && typeof entry === "object" ? entry : directoryEntry || entry;
    const label = String(displayName(candidate) || "").trim();
    const key = id || label.toLowerCase();
    if (!key || uniqueEntries.has(key)) return;
    uniqueEntries.set(key, { id, label: label || id || "-" });
  });

  return Array.from(uniqueEntries.values());
};

const getEntitySummary = (entity) => {
  if (!entity) {
    return { id: "", name: "-", uniqueId: "-", mobileNo: "-" };
  }

  return {
    id: String(entity?._id || entity?.id || "").trim(),
    name: entity?.username || entity?.name || entity?.email || entity?.uniqueId || "-",
    // Login ID is the user-created identifier. Never substitute an email here:
    // generated `@user.local` addresses are not valid login IDs for display or
    // copy from this device-user popup.
    uniqueId: [entity?.uniqueId, entity?.userId, entity?.loginId]
      .map((value) => String(value || "").trim())
      .find(Boolean) || "-",
    mobileNo: entity?.mobileNo || entity?.contactNumber || entity?.phone || entity?.mobile || "-",
  };
};

const FULL_DEVICE_ACCESS_ROLES = new Set([
  ROLES.SUPER_ADMIN,
  ROLES.MANAGER,
  ROLES.HR,
  ROLES.OFFICE_EMPLOYEE,
]);

const getRelatedUserIds = (value) => {
  const ids = new Set();
  toArray(value).forEach((entry) => {
    const id = getEntityId(entry);
    if (id) ids.add(id);
  });
  return Array.from(ids);
};

const canAccessDeviceForRole = ({ device, currentUser, currentUserRole }) => {
  if (!device) return false;
  if (
    [
      ROLES.SUB_ADMIN,
      ROLES.DEALER,
      ROLES.CLIENT,
      ROLES.USER,
      ROLES.VIEWER,
    ].includes(currentUserRole)
  ) {
    // Device-list API is already backend-scoped for these roles.
    // Avoid hiding valid rows again on the client when ownership comes via dealer/sub-admin hierarchy.
    return true;
  }
  if (FULL_DEVICE_ACCESS_ROLES.has(currentUserRole)) return true;

  const currentUserId = String(currentUser?._id || currentUser?.id || "").trim();
  if (!currentUserId) return false;

  const relationIds = new Set([
    ...getRelatedUserIds(device?.dealer),
    ...getRelatedUserIds(device?.assignedToUser),
    ...getRelatedUserIds(device?.assignedToUsers),
    ...getRelatedUserIds(device?.assignedToViewers),
    ...getRelatedUserIds(device?.adminId),
    ...getRelatedUserIds(device?.driver?.driverId),
    ...getRelatedUserIds(device?.createdBy),
    ...getRelatedUserIds(device?.ownerUser),
    ...getRelatedUserIds(device?.user),
    ...getRelatedUserIds(device?.customer),
  ]);

  if (relationIds.has(currentUserId)) return true;
  return false;
};

const normalizeFilterValue = (value) => String(value || "").trim().toLowerCase();

const normalizeStatusBucket = (value) => {
  const normalized = normalizeFilterValue(value).replace(/_/g, " ");
  if (["inactive", "not reporting", "notreporting", "offline"].includes(normalized)) return "inactive";
  if (["unknown", "no data", "nodata"].includes(normalized)) return "no data";
  if (["stopped", "stop", "parked"].includes(normalized)) return "stopped";
  if (["idle", "idling"].includes(normalized)) return "idle";
  if (["running", "moving"].includes(normalized)) return "running";
  if (normalized.includes("expired")) return "expired";
  return normalized;
};

const isSameDay = (left, right) =>
  left instanceof Date &&
  right instanceof Date &&
  !Number.isNaN(left.getTime()) &&
  !Number.isNaN(right.getTime()) &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const matchesSummaryBucket = (row, summaryTab) => {
  if (!row || summaryTab === "all") return true;
  const now = new Date();
  const createdAt = row.creationTs instanceof Date ? row.creationTs : null;
  const expiryAt = row.licenseExpiryTs instanceof Date ? row.licenseExpiryTs : null;

  if (summaryTab === "noData") {
    return normalizeStatusBucket(row.statusKey) === "no data";
  }

  if (summaryTab === "today") {
    return isSameDay(createdAt, now);
  }

  if (summaryTab === "month") {
    return (
      createdAt &&
      createdAt.getFullYear() === now.getFullYear() &&
      createdAt.getMonth() === now.getMonth()
    );
  }

  if (summaryTab === "expiringToday") {
    return isSameDay(expiryAt, now);
  }

  if (summaryTab === "expiringWeek") {
    if (!expiryAt) return false;
    const diffDays = Math.ceil((START_OF_DAY(expiryAt) - START_OF_DAY(now)) / 86400000);
    return diffDays >= 0 && diffDays <= 7;
  }

  if (summaryTab === "expiringMonth") {
    if (!expiryAt) return false;
    const diffDays = Math.ceil((START_OF_DAY(expiryAt) - START_OF_DAY(now)) / 86400000);
    return diffDays >= 0 && diffDays <= 30;
  }

  return true;
};

const matchesLocalDeviceFilter = ({ row, filterBy, query, primaryTab, summaryTab }) => {
  if (!row) return false;
  const normalizedQuery = normalizeFilterValue(query);

  if (primaryTab === "deleted") {
    if (!row.deleted) return false;
  } else if (primaryTab === "devices") {
    if (row.deleted) return false;
    if (!matchesSummaryBucket(row, summaryTab)) return false;
  }

  if (!normalizedQuery) return true;

  if (filterBy === "dealer") {
    return row.dealerIds.some((id) => String(id) === String(query)) ||
      normalizeFilterValue(row.dealers).includes(normalizedQuery);
  }

  if (filterBy === "customer") {
    return row.userIds.some((id) => String(id) === String(query)) ||
      normalizeFilterValue(row.users).includes(normalizedQuery);
  }

  if (filterBy === "group") {
    return String(row.groupId || "") === String(query) ||
      normalizeFilterValue(row.groupName).includes(normalizedQuery);
  }

  if (filterBy === "status") {
    return normalizeStatusBucket(row.statusKey) === normalizeStatusBucket(query) ||
      normalizeFilterValue(row.status).includes(normalizedQuery);
  }

  const haystacksByFilter = {
    imei: [row.imei],
    vehicle: [row.vehicle, row.vehicleNumber, row.vehicleName, row.raw?.vehicleNumber, row.raw?.name, row.raw?.deviceId],
    sim: [row.simNo],
    model: [row.model],
    vehicleType: [row.raw?.vehicleType, row.icon],
    deleted: [row.disabled, row.deleted ? "deleted yes true" : "active no false"],
    contact: [row.contact],
    creationDate: [row.creationTime],
  };

  const haystack = (haystacksByFilter[filterBy] || [
    row.vehicle,
    row.imei,
    row.simNo,
    row.model,
    row.contact,
  ])
    .filter(Boolean)
    .join(" ");

  return normalizeFilterValue(haystack).includes(normalizedQuery);
};

const DEFAULT_NOTIFICATION_CHANNELS = ["socket", "push", "web"];

function DeviceNotificationSettingsDialog({ device, open, onClose, showStatus }) {
  const [search, setSearch] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const deviceId = device?._id || device?.id || null;
  const {
    data: templatePayload,
    isFetching,
    refetch: refetchTemplate,
  } = useGetDeviceNotificationTemplateQuery(deviceId, { skip: !open || !deviceId });
  const [createNotificationSetting] = useCreateNotificationSettingMutation();
  const [updateNotificationSetting] = useUpdateNotificationSettingMutation();

  const canEdit = Boolean(templatePayload?.canEdit);
  const ownerName = templatePayload?.ownerUser?.username || "";
  const templateRows = useMemo(
    () => (Array.isArray(templatePayload?.alerts) ? templatePayload.alerts : []),
    [templatePayload]
  );

  const visibleRows = useMemo(() => {
    const query = normalizeFilterValue(search);
    if (!query) return templateRows;
    return templateRows.filter((item) => normalizeFilterValue(item.label).includes(query));
  }, [search, templateRows]);

  const handleToggle = async (item, checked) => {
    if (!deviceId || !canEdit) return;

    const nextChannels = checked
      ? toArray(item.channels).filter(Boolean).length
        ? Array.from(new Set(toArray(item.channels).filter(Boolean)))
        : DEFAULT_NOTIFICATION_CHANNELS
      : [];
    const payload = {
      alarmType: item.alarmType,
      allDevices: false,
      deviceId,
      channels: nextChannels,
      isActive: nextChannels.length > 0,
      sound: item?.sound || "default",
      priority: item?.priority || "medium",
    };

    setBusyKey(item.key);
    try {
      if (item?.settingId && item?.scope === "device") {
        await updateNotificationSetting({ id: item.settingId, ...payload }).unwrap();
      } else {
        await createNotificationSetting(payload).unwrap();
      }
      await refetchTemplate();
      showStatus("success", `${item.label} notification ${checked ? "enabled" : "disabled"}.`);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update notification setting.");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busyKey ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
    >
      <Box sx={{ px: 3, py: 2.5, bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 28, sm: 38 }, lineHeight: 1, fontWeight: 900, color: "#111" }}>
            Notifications
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 14, color: "#6b7280", fontWeight: 600 }}>
            {device?.name || device?.vehicleNumber || device?.imei || "Selected device"}
          </Typography>
          {!canEdit && ownerName ? (
            <Typography sx={{ mt: 0.75, fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
              Controlled by {ownerName}. View only.
            </Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose} disabled={Boolean(busyKey)} sx={{ color: "#111", mt: -0.5, mr: -1 }}>
          <CloseIcon sx={{ fontSize: 34 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#f7f7f6", px: 3, py: 2.5 }}>
        <TextField
          fullWidth
          placeholder="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              bgcolor: "#fff",
              borderRadius: 1.5,
              fontSize: 22,
              fontWeight: 700,
              pr: 1,
            },
            "& .MuiOutlinedInput-input": {
              py: 1.8,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "#ff5f7e", fontSize: 42 }} />
              </InputAdornment>
            ),
          }}
        />

        {isFetching ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {visibleRows.map((item) => {
              const checked = Boolean(item?.checked);
              const saving = busyKey === item.key;

              return (
                <Box
                  key={item.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: 46,
                  }}
                >
                  <Checkbox
                    checked={checked}
                    disabled={saving || !canEdit}
                    onChange={(event) => handleToggle(item, event.target.checked)}
                    sx={{
                      p: 0.5,
                      color: "#1976d2",
                      "&.Mui-checked": { color: "#1976d2" },
                    }}
                  />
                  <Typography sx={{ fontSize: 23, lineHeight: 1.2, color: "#1f2937", fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  {saving ? <CircularProgress size={16} sx={{ ml: 0.75 }} /> : null}
                </Box>
              );
            })}

            {!visibleRows.length ? (
              <Typography sx={{ py: 3, textAlign: "center", color: "#6b7280", fontWeight: 600 }}>
                No notifications found.
              </Typography>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

const buildCsv = (rows) => {
  const headers = [
    "Vehicle",
    "IMEI",
    "Group",
    "Status",
    "Last Update",
    "Dealer",
    "User",
    "Created By",
    "Sim No",
    "Model",
    "Contact",
    "Icon",
    "Disabled",
    "License Expiration Time",
    "Creation Time",
    "Todays Odo",
    "Total Odo",
    "License Type",
    "Remarks",
    "Integration",
  ];

  const body = rows.map((row) =>
    [
      row.vehicle,
      row.imei,
      row.groupName,
      row.status,
      row.lastUpdate,
      row.dealers,
      row.users,
      row.createdBy,
      row.simNo,
      row.model,
      row.contact,
      row.icon,
      row.disabled,
      row.licenseExpiryTime,
      row.creationTime,
      row.todaysOdo,
      row.totalOdo,
      row.licenseType,
      row.remarks,
      row.integration,
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );

  return [headers.join(","), ...body].join("\n");
};

const stickySx = (left, width, zIndex = 3) => ({
  position: "sticky",
  left,
  minWidth: width,
  width,
  zIndex,
  backgroundColor: "#fff",
  boxShadow: "1px 0 0 #e5e7eb",
});

const DeviceListPortalModal = ({ open = true, onClose }) => {
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserRole = normalizeRole(currentUser?.role);
  const deviceActions = getDeviceActionsEffective(currentUser);
  const { showStatus } = useStatus();
  const fileInputRef = useRef(null);
  const availableTabs = deviceActions.canDelete ? PRIMARY_TABS : PRIMARY_TABS.filter((tab) => tab.value !== "deleted");
  const [primaryTab, setPrimaryTab] = useState("devices");
  const includeDeleted = primaryTab === "deleted";

  const [summaryTab, setSummaryTab] = useState("all");
  const [filterBy, setFilterBy] = useState("imei");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [filterInputValue, setFilterInputValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [deviceDetailCache, setDeviceDetailCache] = useState({});
  const [cachedPageData, setCachedPageData] = useState(null);
  const [renewalFromDate, setRenewalFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [renewalFromHour, setRenewalFromHour] = useState(0);
  const [renewalFromMin, setRenewalFromMin] = useState(0);
  const [renewalToDate, setRenewalToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [renewalToHour, setRenewalToHour] = useState(23);
  const [renewalToMin, setRenewalToMin] = useState(59);
  const [renewalVehicleId, setRenewalVehicleId] = useState(null);
  const [renewalVehicleInput, setRenewalVehicleInput] = useState("");
  const [renewalQuick, setRenewalQuick] = useState("Today");
  const [renewalApplied, setRenewalApplied] = useState(null);
  const [expiringCategory, setExpiringCategory] = useState("7");
  const [expiringFromDate, setExpiringFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [expiringToDate, setExpiringToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expiringQuick, setExpiringQuick] = useState("Week");
  const [expiringApplied, setExpiringApplied] = useState(null);
  const filterUsesDropdown = ["customer", "dealer", "status", "group"].includes(filterBy);
  const fetchLimit = rowsPerPage === -1 ? 0 : rowsPerPage;
  const trimmedSearchQuery = String(searchQuery || "").trim();

  useDevRenderInstrumentation("DeviceListPortalModal", () => ({
    open,
    primaryTab,
    summaryTab,
    page,
    rowsPerPage,
    filterBy,
  }));

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(trimmedSearchQuery);
    }, filterUsesDropdown ? 0 : 200);
    return () => clearTimeout(timer);
  }, [filterUsesDropdown, trimmedSearchQuery]);

  const renewalQueryArgs = useMemo(() => {
    if (primaryTab !== "renewal") return null;
    const args = {
      page,
      limit: fetchLimit > 0 ? Math.min(fetchLimit, 100) : 0,
      all: rowsPerPage === -1 ? "true" : undefined,
    };
    if (renewalApplied?.fromTs instanceof Date && !Number.isNaN(renewalApplied.fromTs.getTime())) {
      args.from = renewalApplied.fromTs.toISOString();
    }
    if (renewalApplied?.toTs instanceof Date && !Number.isNaN(renewalApplied.toTs.getTime())) {
      args.to = renewalApplied.toTs.toISOString();
    }
    if (renewalApplied?.vehicleId) args.vehicleId = renewalApplied.vehicleId;
    return args;
  }, [fetchLimit, page, primaryTab, renewalApplied, rowsPerPage]);

  const expiringQueryArgs = useMemo(() => {
    if (primaryTab !== "expiring") return null;
    const args = {
      page,
      limit: fetchLimit > 0 ? Math.min(fetchLimit, 100) : 0,
      all: rowsPerPage === -1 ? "true" : undefined,
      category: expiringCategory,
    };
    if (expiringApplied?.fromTs instanceof Date && !Number.isNaN(expiringApplied.fromTs.getTime())) {
      args.from = expiringApplied.fromTs.toISOString();
    }
    if (expiringApplied?.toTs instanceof Date && !Number.isNaN(expiringApplied.toTs.getTime())) {
      args.to = expiringApplied.toTs.toISOString();
    }
    return args;
  }, [expiringApplied, expiringCategory, fetchLimit, page, primaryTab, rowsPerPage]);

  const devicesQueryArgs = useMemo(() => {
    if (primaryTab !== "devices" && primaryTab !== "deleted") return null;

    const args = {
      scopeUserId: String(currentUser?._id || currentUser?.id || ""),
      page,
      limit: fetchLimit > 0 ? Math.min(fetchLimit, 100) : 0,
      all: rowsPerPage === -1 ? "true" : undefined,
      includeDeleted: includeDeleted ? "true" : "false",
      summaryTab: primaryTab === "devices" ? summaryTab : undefined,
      status: includeDeleted ? "deleted" : undefined,
    };

    if (filterBy === "dealer" && debouncedSearchQuery) {
      args.dealer = debouncedSearchQuery;
    } else if (filterBy === "customer" && debouncedSearchQuery) {
      args.customer = debouncedSearchQuery;
    } else if (filterBy === "group" && debouncedSearchQuery) {
      args.group = debouncedSearchQuery;
    } else if (filterBy === "status" && debouncedSearchQuery) {
      args.status = debouncedSearchQuery;
    } else if (debouncedSearchQuery) {
      args.search = debouncedSearchQuery;
      args.filterBy = filterBy;
    }

    return args;
  }, [currentUser?._id, currentUser?.id, debouncedSearchQuery, fetchLimit, filterBy, includeDeleted, page, primaryTab, rowsPerPage, summaryTab]);

  const activeQueryArgs =
    primaryTab === "renewal"
      ? renewalQueryArgs
      : primaryTab === "expiring"
        ? expiringQueryArgs
        : devicesQueryArgs;

  const {
    data: devicesPageResponse,
    refetch: refetchDevicesPage,
    isFetching: devicesPageFetching,
    isLoading: devicesPageLoading,
    isError: devicesPageError,
  } = useGetDevicesPageQuery(
    primaryTab === "devices" || primaryTab === "deleted" ? devicesQueryArgs : skipToken,
    { refetchOnMountOrArgChange: true }
  );
  const {
    data: renewalsPageResponse,
    refetch: refetchRenewalsPage,
    isFetching: renewalsPageFetching,
    isLoading: renewalsPageLoading,
    isError: renewalsPageError,
  } = useGetDeviceRenewalsPageV2Query(primaryTab === "renewal" ? renewalQueryArgs : skipToken);
  const {
    data: expiringPageResponse,
    refetch: refetchExpiringPage,
    isFetching: expiringPageFetching,
    isLoading: expiringPageLoading,
    isError: expiringPageError,
  } = useGetDeviceExpiringPageV2Query(primaryTab === "expiring" ? expiringQueryArgs : skipToken);
  const {
    data: fleetSummary,
  } = useGetFleetSummaryQuery(undefined, {
    skip: !open || primaryTab !== "devices",
    refetchOnMountOrArgChange: true,
  });

  const livePageResponse =
    primaryTab === "renewal"
      ? renewalsPageResponse
      : primaryTab === "expiring"
        ? expiringPageResponse
        : devicesPageResponse;
  const pageData = livePageResponse || cachedPageData;
  const refetch =
    primaryTab === "renewal"
      ? refetchRenewalsPage
      : primaryTab === "expiring"
        ? refetchExpiringPage
        : refetchDevicesPage;
  const devicesLoading =
    primaryTab === "renewal"
      ? renewalsPageLoading
      : primaryTab === "expiring"
        ? expiringPageLoading
        : devicesPageLoading;
  const devicesFetching =
    primaryTab === "renewal"
      ? renewalsPageFetching
      : primaryTab === "expiring"
        ? expiringPageFetching
        : devicesPageFetching;
  const devicesError =
    primaryTab === "renewal"
      ? renewalsPageError
      : primaryTab === "expiring"
        ? expiringPageError
        : devicesPageError;
  // Keep the table stable during refreshes. The list gets one centered loader
  // only for its first uncached response, instead of scattered loading UI.
  const showCenteredTableLoader = Boolean(
    (devicesLoading || devicesFetching) && !pageData
  );
  const serverTotal = Number(pageData?.total || 0);
  const { data: assignableUsersPayload = [] } = useGetAssignableUsersQuery(["viewer", "client", "user", "group_admin"], {
    skip: !open,
  });
  const { data: allUsersPayload = [] } = useGetAllUsersQuery(undefined, {
    skip: !open,
  });
  const users = useMemo(
    () => (Array.isArray(assignableUsersPayload) ? assignableUsersPayload : []),
    [assignableUsersPayload]
  );
  const allUsers = useMemo(
    () => (Array.isArray(allUsersPayload) ? allUsersPayload : []),
    [allUsersPayload]
  );
  const { data: groupsPayload = [] } = useGetAllGroupsQuery();
  const [editDevice, { isLoading: updatingDevice }] = useEditDeviceMutation();
  const [deleteDevice] = useDeleteDeviceMutation();
  const [bulkDeleteDevices, { isLoading: bulkDeleting }] = useBulkDeleteDevicesMutation();
  const [restoreDevice] = useRestoreDeviceMutation();
  const [permanentlyDeleteDevice] = usePermanentlyDeleteDeviceMutation();
  const [addDevicesToGroup] = useAddDevicesToGroupMutation();
  const [loadDeviceDetail] = useLazyGetDeviceV2ByIdQuery();
  const [revealUserPassword] = useRevealUserPasswordMutation();

  useEffect(() => {
    if (!open) return;
    const cachedPayload = readDeviceViewCache(DEVICE_LIST_MODAL_PAGE_CACHE_SCOPE, {
      userId: currentUser?._id,
      params: activeQueryArgs || {},
    });
    if (cachedPayload?.data && Array.isArray(cachedPayload.data)) {
      setCachedPageData(cachedPayload);
      return;
    }

    setCachedPageData(null);
  }, [
    activeQueryArgs,
    currentUser?._id,
    debouncedSearchQuery,
    includeDeleted,
    open,
    page,
    primaryTab,
    rowsPerPage,
    summaryTab,
  ]);

  useEffect(() => {
    const nextPayload = livePageResponse;
    if (!nextPayload?.data || !Array.isArray(nextPayload.data)) return;

    writeDeviceViewCache(DEVICE_LIST_MODAL_PAGE_CACHE_SCOPE, {
      userId: currentUser?._id,
      params: activeQueryArgs || {},
      payload: nextPayload,
    });
  }, [activeQueryArgs, currentUser?._id, livePageResponse]);

  const [modalMode, setModalMode] = useState("table");
  const [editingDevice, setEditingDevice] = useState(null);
  const [notificationDevice, setNotificationDevice] = useState(null);
  const [reportDevice, setReportDevice] = useState(null);
  const [certificateDevice, setCertificateDevice] = useState(null);
  const [sensorDevice, setSensorDevice] = useState(null);
  const [assignUserDevice, setAssignUserDevice] = useState(null);
  const [assignUserId, setAssignUserId] = useState("");
  const [assignGroupDevice, setAssignGroupDevice] = useState(null);
  const [assignGroupId, setAssignGroupId] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuDevice, setMenuDevice] = useState(null);
  const [editingOdo, setEditingOdo] = useState(null);
  const [editingOdoValue, setEditingOdoValue] = useState("");
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [extendValidityDevice, setExtendValidityDevice] = useState(null);
  const [extendValidityYears, setExtendValidityYears] = useState(1);
  const [extendValidityRemark, setExtendValidityRemark] = useState("");
  const [extendValidityFrom, setExtendValidityFrom] = useState("current");
  const [userInfoDialog, setUserInfoDialog] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [loadingPasswordIds, setLoadingPasswordIds] = useState(new Set());

  const baseDevices = useMemo(() => {
    return Array.isArray(pageData?.data) ? pageData.data : [];
  }, [pageData]);
  const devices = useMemo(
    () =>
      baseDevices.map((device) => {
        const deviceId = String(device?._id || device?.id || "");
        return deviceDetailCache[deviceId]
          ? mergeLegacyDeviceDetail(device, deviceDetailCache[deviceId])
          : device;
      }),
    [baseDevices, deviceDetailCache]
  );
  const visibleDevices = useMemo(
    () =>
      devices
        .filter((device) =>
          canAccessDeviceForRole({
            device,
            currentUser,
            currentUserRole,
          })
        )
        .sort((a, b) => {
          const timeDiff = getDeviceRecencyTs(b) - getDeviceRecencyTs(a);
          if (timeDiff !== 0) return timeDiff;
          return String(b?._id || b?.id || "").localeCompare(String(a?._id || a?.id || ""));
        }),
    [currentUser, currentUserRole, devices]
  );
  const ensureDeviceDetail = async (device) => {
    const deviceId = String(device?._id || device?.id || "").trim();
    if (!deviceId) return device;
    if (deviceDetailCache[deviceId]) {
      return mergeLegacyDeviceDetail(device, deviceDetailCache[deviceId]);
    }
    const detailed = await loadDeviceDetail(deviceId, true).unwrap();
    if (detailed) {
      setDeviceDetailCache((prev) => ({ ...prev, [deviceId]: detailed }));
      return mergeLegacyDeviceDetail(device, detailed);
    }
    return device;
  };
  const groups = useMemo(() => {
    const rows = groupsPayload?.data || groupsPayload || [];
    return Array.isArray(rows) ? rows : [];
  }, [groupsPayload]);

  const userDirectoryById = useMemo(() => {
    const map = new Map();
    [...users, ...allUsers].forEach((user) => {
      const id = String(user?._id || user?.id || "").trim();
      if (!id || map.has(id)) return;
      map.set(id, user);
    });
    return map;
  }, [allUsers, users]);



  const applyRenewalQuick = (quick) => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    if (quick === "Today") {
      setRenewalFromDate(todayStr);
      setRenewalToDate(todayStr);
    } else if (quick === "Week") {
      const s = new Date(now); s.setDate(s.getDate() - 7);
      setRenewalFromDate(s.toISOString().slice(0, 10));
      setRenewalToDate(todayStr);
    } else if (quick === "Month") {
      const s = new Date(now); s.setMonth(s.getMonth() - 1);
      setRenewalFromDate(s.toISOString().slice(0, 10));
      setRenewalToDate(todayStr);
    }
    setRenewalQuick(quick);
  };

  const applyExpiringQuick = (quick) => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    if (quick === "Week") {
      const s = new Date(now); s.setDate(s.getDate() - 7);
      setExpiringFromDate(s.toISOString().slice(0, 10));
      setExpiringToDate(todayStr);
    } else if (quick === "Month") {
      const s = new Date(now); s.setMonth(s.getMonth() - 1);
      setExpiringFromDate(s.toISOString().slice(0, 10));
      setExpiringToDate(todayStr);
    }
    setExpiringQuick(quick);
  };

  const handleRenewalSearch = () => {
    const fromTs = new Date(
      `${renewalFromDate}T${String(renewalFromHour).padStart(2, "0")}:${String(renewalFromMin).padStart(2, "0")}:00`
    );
    const toTs = new Date(
      `${renewalToDate}T${String(renewalToHour).padStart(2, "0")}:${String(renewalToMin).padStart(2, "0")}:59`
    );
    setRenewalApplied({ fromTs, toTs, vehicleId: renewalVehicleId });
    setPage(1);
  };

  const handleExpiringSearch = () => {
    const fromTs = new Date(`${expiringFromDate}T00:00:00`);
    const toTs = new Date(`${expiringToDate}T23:59:59`);
    setExpiringApplied({ fromTs, toTs });
    setPage(1);
  };


  const groupById = useMemo(() => {
    const entries = groups.map((group) => [String(group?._id || ""), group]);
    return new Map(entries.filter(([id]) => id));
  }, [groups]);
  const deviceGroupMembership = useMemo(() => {
    const membership = new Map();
    groups.forEach((group) => {
      const groupId = String(group?._id || "");
      const groupName = String(group?.name || "").trim();
      (Array.isArray(group?.devices) ? group.devices : []).forEach((deviceId) => {
        const normalizedDeviceId = String(deviceId?._id || deviceId || "").trim();
        if (normalizedDeviceId && !membership.has(normalizedDeviceId)) {
          membership.set(normalizedDeviceId, { groupId, groupName });
        }
      });
    });
    return membership;
  }, [groups]);

  const normalizedRows = useMemo(
    () =>
      visibleDevices.map((device) => {
        const licenseInfo = getLicenseInfo(device);
        const live = device?.liveData || device?.deviceLive || device?.liveSnapshot || {};
        const latestRenewal = device?.latestRenewal || null;
        const dealerEntriesResolved = getUniqueEntityEntriesWithDirectory([
          ...toArray(device?.dealer),
          ...toArray(device?.dealerOwner),
          ...toArray(device?.dealerName),
        ], userDirectoryById);
        const createdByEntries = getUniqueEntityEntriesWithDirectory([
          ...toArray(device?.createdBy),
          ...toArray(device?.adminId),
        ], userDirectoryById);
        const userEntries = getUniqueEntityEntriesWithDirectory([
          ...toArray(device?.assignedToUser),
          ...toArray(device?.assignedToUsers),
          ...toArray(device?.assignedToViewers),
          ...toArray(device?.ownerUser),
          ...toArray(device?.user),
          ...toArray(device?.customer),
          ...toArray(device?.assignedToUsername),
          ...toArray(device?.assignedToUserName),
          ...toArray(device?.userName),
          ...toArray(device?.customerName),
          ...toArray(device?.ownerUserName),
        ], userDirectoryById);
        const dealerNames =
          dealerEntriesResolved.map((entry) => entry.label).filter(Boolean).join(", ") ||
          String(device?.dealerName || "").trim() ||
          displayName(device?.dealer) ||
          displayName(userDirectoryById.get(String(device?.dealerOwner || "").trim())) ||
          "-";
        const userNames =
          userEntries.map((entry) => entry.label).filter(Boolean).join(", ") ||
          String(device?.assignedToUsername || "").trim() ||
          String(device?.assignedToUserName || "").trim() ||
          displayName(userDirectoryById.get(String(device?.assignedToUser || "").trim())) ||
          displayName(userDirectoryById.get(String(device?.ownerUser || "").trim())) ||
          displayName(userDirectoryById.get(String(device?.user || "").trim())) ||
          displayName(userDirectoryById.get(String(device?.customer || "").trim())) ||
          "-";
        const rawGroupRef = device?.deviceGroup || device?.group;
        const rawGroupId = String(rawGroupRef?._id || device?.groupId || rawGroupRef || "").trim();
        const membershipGroup = deviceGroupMembership.get(String(device?._id || device?.id || "").trim());
        const resolvedGroupId = rawGroupId || membershipGroup?.groupId || "";
        const resolvedGroup = resolvedGroupId ? groupById.get(resolvedGroupId) : null;
        const groupName =
          device?.groupName ||
          resolvedGroup?.name ||
          membershipGroup?.groupName ||
          (rawGroupRef ? displayName(rawGroupRef) : "-");
        const createdAt = normalizeDate(device?.createdAt);
        const resolvedVehicleStatus = String(resolveVehicleStatus(device) || "no_data")
          .toLowerCase()
          .replace(/_/g, " ")
          .trim();
        const freshestTs = Math.max(
          toTimestampMs(device?.lastDataReceivedAt),
          toTimestampMs(live?.packetReceivedAt),
          toTimestampMs(live?.lastSocketUpdate),
          toTimestampMs(live?.packetTime),
          toTimestampMs(live?.timestamp),
          toTimestampMs(live?.serverTime),
          toTimestampMs(live?.lastUpdate)
        );
        const normalizedResolvedStatus = normalizeStatusBucket(resolvedVehicleStatus);
        const hasNeverReportedData =
          normalizedResolvedStatus === "no data" ||
          (freshestTs <= 0 && normalizedResolvedStatus !== "expired");
        const effectiveStatus = hasNeverReportedData ? "no data" : resolvedVehicleStatus;
        const statusKey = normalizeStatusBucket(effectiveStatus) || "no data";
        const deleted =
          String(device?.status || "").toLowerCase() === "deleted" ||
          Boolean(device?.deletedAt) ||
          Boolean(device?.isDeleted) ||
          device?.deleted === true;

        const createdById =
          String(device?.createdBy?._id || device?.createdBy?.id || device?.createdBy || "").trim() ||
          String(device?.adminId?._id || device?.adminId?.id || device?.adminId || "").trim() ||
          String(device?.dealerOwner?._id || device?.dealerOwner?.id || device?.dealerOwner || "").trim();
        const createdByEntity = createdById ? userDirectoryById.get(createdById) : null;

        return {
          raw: device,
          id: device?._id || device?.id,
          vehicle: device?.name || device?.vehicleNumber || device?.deviceId || "-",
          vehicleNumber: device?.vehicleNumber || device?.name || device?.deviceId || "-",
          vehicleName: device?.name || device?.vehicleNumber || "-",
          imei: device?.imei || device?.deviceId || "-",
          groupId: resolvedGroupId,
          groupName,
          statusKey,
          status: hasNeverReportedData ? "No Data" : getDisplayDeviceStatus(device, licenseInfo),
          lastUpdate: hasNeverReportedData ? "-" : formatDate(device?.lastDataReceivedAt || live?.timestamp, true),
          lastUpdateTs: hasNeverReportedData ? null : normalizeDate(device?.lastDataReceivedAt || live?.timestamp),
          dealerIds: dealerEntriesResolved.map((entry) => entry.id).filter(Boolean),
          dealers: dealerNames,
          dealerEntries: dealerEntriesResolved.map((entry) => ({
            ...entry,
            user: entry?.id ? userDirectoryById.get(String(entry.id)) || null : null,
          })),
          userIds: userEntries.map((entry) => entry.id).filter(Boolean),
          users: userNames,
          userEntries: userEntries.map((entry) => ({
            ...entry,
            user: entry?.id ? userDirectoryById.get(String(entry.id)) || null : null,
          })),
          createdBy:
            createdByEntries.map((entry) => entry.label).filter(Boolean).join(", ") ||
            String(device?.createdByName || "").trim() ||
            resolveCreatedByLabel(device?.createdBy) ||
            resolveCreatedByLabel(device?.adminId) ||
            resolveCreatedByLabel(device?.dealerOwner) ||
            "-",
          createdByEntry: createdByEntries.length
            ? createdByEntries.map((entry) => ({
                ...entry,
                user: entry?.id ? userDirectoryById.get(String(entry.id)) || null : null,
              }))
            : createdByEntity
              ? [{
                  id: createdById,
                  label: displayName(createdByEntity),
                  user: createdByEntity,
                }]
              : [],
          simNo:
            device?.simNo ||
            device?.msdn ||
            device?.simNumber ||
            device?.sim ||
            device?.simCard?.MSDN ||
            device?.asset?.simCard?.MSDN ||
            "-",
          model: device?.model || "-",
          contact: device?.contact || device?.driverContact || device?.driver?.contactNumber || device?.contactNumber || "-",
          icon: device?.vehicleType || device?.deviceIcon || "Default",
          disabled: deleted ? "Yes" : "No",
          deleted,
          licenseExpiryTime: formatDate(licenseInfo.expiryDate),
          licenseExpiryTs: licenseInfo.expiresAt,
          creationTime: formatDate(device?.createdAt),
          creationTs: createdAt,
          todaysOdo: numberValue(device?.todaysOdo).toFixed(0),
          totalOdo: numberValue(device?.totalOdo).toFixed(2).replace(/\.00$/, ""),
          licenseType: licenseInfo.type,
          hasBeenRenewed: Boolean(latestRenewal?.renewedAt),
          renewedAt: normalizeDate(latestRenewal?.renewedAt),
          renewalPreviousExpiry: normalizeDate(latestRenewal?.previousExpiryDate),
          renewalNextExpiry: normalizeDate(latestRenewal?.nextExpiryDate),
          renewalRemarks: latestRenewal?.remarks || "",
          renewalFrom: latestRenewal?.renewFrom || "",
          remarks: extractRemarks(device) || latestRenewal?.remarks || "-",
          integration: String(device?.integrationEnabled ?? device?.integration ?? false),
          expiryDiffDays: device?.expiryDiffDays ?? licenseInfo.diffDays,
        };
      }),
    [deviceGroupMembership, groupById, userDirectoryById, visibleDevices]
  );

  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
  const MIN_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

  const vehicleOptions = useMemo(
    () =>
      normalizedRows
        .filter((r) => !r.deleted)
        .map((r) => ({ value: r.id, label: `${r.vehicle} — ${r.imei}` })),
    [normalizedRows]
  );

  const summaryCounts = useMemo(
    () => {
      const summaryApiCounts = fleetSummary?.counts || {};
      const summaryApiTotal = Number(fleetSummary?.total || summaryApiCounts?.total || 0);
      const summaryHasCounts = summaryApiTotal > 0;
      return {
        all: pageData?.counts?.all ?? (summaryHasCounts ? summaryApiTotal : serverTotal || normalizedRows.length),
        deleted: pageData?.counts?.deleted ?? null,
        noData:
          pageData?.counts?.noData ??
          (summaryHasCounts ? Number(summaryApiCounts?.no_data || 0) : null) ??
          normalizedRows.filter((row) => !row.deleted && matchesSummaryBucket(row, "noData")).length,
        today: pageData?.counts?.today ?? null,
        month: pageData?.counts?.month ?? null,
        expiringToday: pageData?.counts?.expiringToday ?? null,
        expiringWeek: pageData?.counts?.expiringWeek ?? null,
        expiringMonth: pageData?.counts?.expiringMonth ?? null,
        expiring7: pageData?.counts?.expiring7 ?? null,
        expiring15: pageData?.counts?.expiring15 ?? null,
        expiring30: pageData?.counts?.expiring30 ?? null,
        expiring60: pageData?.counts?.expiring60 ?? null,
        expiredCount: pageData?.counts?.expiredCount ?? null,
      };
    },
    [fleetSummary?.counts, fleetSummary?.total, normalizedRows, pageData?.counts, serverTotal]
  );

  const filterSelectOptions = useMemo(() => {
    if (filterBy === "customer") {
      const options = users
        .filter((user) => {
          const role = normalizeRole(user?.role);
          return role === ROLES.USER || role === ROLES.CLIENT;
        })
        .map((user) => {
          const label = user?.username || user?.name || user?.mobileNo || user?.email || "User";
          return {
            value: String(user?._id || user?.id || label),
            label,
            searchText: [user?.username, user?.name, user?.mobileNo, user?.email, user?.role].filter(Boolean).join(" "),
          };
        });
      return Array.from(new Map(options.map((option) => [option.value, option])).values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (filterBy === "dealer") {
      const options = users
        .filter((user) => {
          const role = normalizeRole(user?.role);
          return role === ROLES.DEALER || role === "group_admin";
        })
        .map((user) => {
          const label = user?.username || user?.name || user?.mobileNo || user?.email || "Dealer";
          return {
            value: String(user?._id || user?.id || label),
            label,
            searchText: [user?.username, user?.name, user?.mobileNo, user?.email, user?.role].filter(Boolean).join(" "),
          };
        });
      return Array.from(new Map(options.map((option) => [option.value, option])).values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (filterBy === "group") {
      return Array.from(new Map(groups
        .map((group) => {
          const label = group?.name || "Group";
          return {
            value: String(group?._id || label),
            label,
            searchText: [group?.name, group?.description].filter(Boolean).join(" "),
          };
        })
        .map((option) => [option.value, option])).values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (filterBy === "status") {
      return STATUS_FILTER_OPTIONS;
    }

    return [];
  }, [filterBy, groups, users]);

  const filteredRows = useMemo(() => {
    return normalizedRows;
  }, [normalizedRows]);

  const selectedFilterOption = useMemo(() => {
    if (!filterUsesDropdown || !searchQuery) return null;
    return (
      filterSelectOptions.find((option) => String(option.value) === String(searchQuery)) ||
      null
    );
  }, [filterSelectOptions, filterUsesDropdown, searchQuery]);

  // In server-paged mode the fetched `filteredRows` already IS the current page from server,
  // so skip client slicing and use server total for pagination / display range.
  const effectiveTotalCount = serverTotal || filteredRows.length;
  const totalPages = getTotalPagesForRows(effectiveTotalCount, rowsPerPage);
  const pagedRows = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredRows;
    }

    return filteredRows;
  }, [filteredRows, rowsPerPage]);
  const displayRange = useMemo(
    () => getDisplayRange(effectiveTotalCount, page, rowsPerPage),
    [effectiveTotalCount, page, rowsPerPage]
  );
  const showSelectionColumn = primaryTab !== "deleted" && deviceActions.canDelete;
  const selectedIdSet = useMemo(() => new Set(selectedDeviceIds), [selectedDeviceIds]);
  const selectablePageRows = useMemo(() => pagedRows.filter((row) => !row.deleted && row.id), [pagedRows]);
  const allPageSelected = selectablePageRows.length > 0 && selectablePageRows.every((row) => selectedIdSet.has(row.id));
  const somePageSelected = selectablePageRows.some((row) => selectedIdSet.has(row.id)) && !allPageSelected;

  const formatOdoCell = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0";
    return numeric.toFixed(2).replace(/\.00$/, "");
  };

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);


  useEffect(() => {
    setCachedPageData(null);
    setRenewalApplied(null);
    setExpiringApplied(null);
    setPage(1);
  }, [primaryTab]);


  useEffect(() => {
    setSelectedDeviceIds((prev) =>
      prev.filter((id) => normalizedRows.some((row) => row.id === id && !row.deleted))
    );
  }, [normalizedRows]);

  useEffect(() => {
    setSelectedDeviceIds([]);
  }, [primaryTab, searchQuery, filterBy, summaryTab]);

  const closeForm = () => {
    setEditingDevice(null);
    setModalMode("table");
  };

  const closeExtendValidityDialog = () => {
    setExtendValidityDevice(null);
    setExtendValidityYears(1);
    setExtendValidityRemark("");
    setExtendValidityFrom("current");
  };

  const openExtendValidityDialog = (row) => {
    if (currentUserRole === ROLES.USER) {
      showStatus("error", "Not allowed: User accounts cannot update device details or extend validity.");
      setMenuAnchorEl(null);
      setMenuDevice(null);
      return;
    }
    setExtendValidityDevice(row?.raw || null);
    setExtendValidityYears(1);
    setExtendValidityRemark("");
    setExtendValidityFrom("current");
    setMenuAnchorEl(null);
    setMenuDevice(null);
  };

  const handleCsvDownload = () => {
    const csv = buildCsv(filteredRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "devices-list.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Devices CSV downloaded.");
  };

  const handleTemplateDownload = async () => {
    try {
      await downloadEntityTemplate("fleets");
      showStatus("success", "Fleet template downloaded.");
    } catch (error) {
      showStatus("error", error?.response?.data?.message || "Template download failed.");
    }
  };

  const handleImportDevices = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    try {
      const result = await uploadEntityFile("fleets", file, { validateOnly: false });
      await refetch();
      showStatus(
        "success",
        result?.message ||
          `Devices import completed (${result?.count || 0} imported${result?.skippedCount ? `, ${result.skippedCount} skipped` : ""}).`
      );
    } catch (error) {
      const payload = error?.response?.data;
      const details = Array.isArray(payload?.errors) ? payload.errors.slice(0, 4).join(" | ") : "";
      showStatus("error", `${payload?.message || "Devices import failed."}${details ? ` ${details}` : ""}`);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (device) => {
    if (!device?.id) return;
    const confirmText = "Ye device deleted devices tab me move hoga. Continue?";
    if (!window.confirm(confirmText)) return;
    try {
      await deleteDevice(device.id).unwrap();
      setSelectedDeviceIds((prev) => prev.filter((id) => id !== device.id));
      showStatus("success", "Device moved to deleted devices.");
      setMenuAnchorEl(null);
      setMenuDevice(null);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete device.");
    }
  };

  const toggleRowSelection = (deviceId) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
  };

  const toggleSelectCurrentPage = (checked) => {
    const pageIds = selectablePageRows.map((row) => row.id);
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return Array.from(next);
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedDeviceIds.length) {
      showStatus("warning", "Please select at least one device.");
      return;
    }
    if (!window.confirm(`${selectedDeviceIds.length} device(s) ko deleted devices me move karna hai. Continue?`)) return;

    try {
      const response = await bulkDeleteDevices(selectedDeviceIds).unwrap();
      setSelectedDeviceIds([]);
      await refetch();
      showStatus("success", response?.msg || "Selected devices moved to deleted devices.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete selected devices.");
    }
  };

  const handleRestore = async (device) => {
    if (!device?.id) return;
    if (!window.confirm("Is deleted device ko restore karna hai?")) return;
    try {
      await restoreDevice(device.id).unwrap();
      showStatus("success", "Device restored successfully.");
      setMenuAnchorEl(null);
      setMenuDevice(null);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to restore device.");
    }
  };

  const handlePermanentDelete = async (device) => {
    if (!device?.id) return;
    if (!window.confirm("Ye device permanently DB se delete ho jayega. Continue?")) return;
    try {
      await permanentlyDeleteDevice(device.id).unwrap();
      showStatus("success", "Device permanently deleted.");
      setMenuAnchorEl(null);
      setMenuDevice(null);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to permanently delete device.");
    }
  };

  const handleAssignUser = async () => {
    if (!assignUserDevice?.id) {
      showStatus("warning", "Please select a device.");
      return;
    }
    try {
      await editDevice({
        deviceId: assignUserDevice.id,
        assignedToUser: assignUserId || null,
      }).unwrap();
      showStatus("success", "Device user assignment updated.");
      setAssignUserDevice(null);
      setAssignUserId("");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to assign user.");
    }
  };

  const handleAssignGroup = async () => {
    if (!assignGroupDevice?.id || !assignGroupId) {
      showStatus("warning", "Please select a group.");
      return;
    }
    try {
      await addDevicesToGroup({ groupId: assignGroupId, deviceIds: [assignGroupDevice.id] }).unwrap();
      showStatus("success", "Device assigned to group.");
      setAssignGroupDevice(null);
      setAssignGroupId("");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to assign group.");
    }
  };

  const openMoreMenu = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuDevice(row);
  };

  const beginOdoEdit = (row, field) => {
    if (!row?.id) return;
    setEditingOdo({ deviceId: row.id, field });
    setEditingOdoValue(String(Number(row.raw?.[`${field}RawKm`] ?? row.raw?.[field] ?? 0) || 0));
  };

  const cancelOdoEdit = () => {
    setEditingOdo(null);
    setEditingOdoValue("");
  };

  const saveOdoEdit = async () => {
    if (currentUserRole === ROLES.USER) {
      showStatus("error", "Not allowed: User accounts cannot update device details or extend validity.");
      cancelOdoEdit();
      return;
    }
    if (!editingOdo?.deviceId || !editingOdo?.field) return;

    const numericValue = Math.max(0, Number(editingOdoValue) || 0);
    const payload =
      editingOdo.field === "todaysOdo"
        ? { deviceId: editingOdo.deviceId, todaysOdo: numericValue }
        : { deviceId: editingOdo.deviceId, totalOdo: numericValue };

    try {
      await editDevice(payload).unwrap();
      cancelOdoEdit();
      await refetch();
      showStatus("success", "Odometer updated.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update odometer.");
    }
  };

  const extendValidityBaseDate = useMemo(() => {
    if (extendValidityFrom === "original") {
      return (
        normalizeDate(
          extendValidityDevice?.licenseRuntime?.expiryDate ||
            extendValidityDevice?.deviceLicense?.expiryDate
        ) || new Date()
      );
    }
    return new Date();
  }, [extendValidityDevice, extendValidityFrom]);

  const extendValidityNextExpiry = useMemo(
    () => addYears(extendValidityBaseDate, extendValidityYears),
    [extendValidityBaseDate, extendValidityYears]
  );

  const handleExtendValidity = async () => {
    if (currentUserRole === ROLES.USER) {
      showStatus("error", "Not allowed: User accounts cannot update device details or extend validity.");
      closeExtendValidityDialog();
      return;
    }
    const deviceId = extendValidityDevice?._id || extendValidityDevice?.id;
    if (!deviceId) {
      showStatus("warning", "Please select a valid device.");
      return;
    }

    try {
      await editDevice({
        deviceId,
        "deviceLicense.licenseType": "yearly",
        "deviceLicense.validity": Number(extendValidityYears) * 12,
        "deviceLicense.startDate": extendValidityBaseDate,
        "deviceLicense.expiryDate": extendValidityNextExpiry,
        licenseRenewalAction: true,
        licenseRenewalRemark: extendValidityRemark,
        licenseRenewFrom: extendValidityFrom,
      }).unwrap();
      showStatus("success", "Device validity extended successfully.");
      closeExtendValidityDialog();
      await refetch();
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to extend device validity.");
    }
  };

  const handleOpenUserInfoDialog = (title, entries = []) => {
    const normalizedEntries = (Array.isArray(entries) ? entries : [])
      .map((entry) => {
        const id = String(entry?.id || entry?._id || "").trim();
        const entryUser = entry?.user && typeof entry.user === "object" ? entry.user : null;
        const directoryUser = id ? userDirectoryById.get(id) : null;
        // The table can carry a thin user object without uniqueId. Merge it
        // with the directory profile so Login ID always comes from uniqueId.
        const user = directoryUser || entryUser || null;
        const summary = getEntitySummary({
          ...(entry && typeof entry === "object" ? entry : {}),
          ...(entryUser || {}),
          ...(directoryUser || {}),
        });
        return {
          id: id || summary.id,
          label: entry?.label || summary.name || "-",
          user,
          summary,
        };
      })
      .filter((entry) => entry.id || entry.label !== "-");

    if (!normalizedEntries.length) {
      showStatus("info", "No user information available.");
      return;
    }

    setUserInfoDialog({
      title,
      entries: normalizedEntries,
      selectedId: normalizedEntries[0]?.id || "",
    });
  };

  const selectedUserInfoEntry = userInfoDialog?.entries?.find(
    (entry) => String(entry.id) === String(userInfoDialog?.selectedId || "")
  ) || userInfoDialog?.entries?.[0] || null;

  const handleTogglePassword = async (userId) => {
    const targetId = String(userId || "").trim();
    if (!targetId) return;

    if (Object.prototype.hasOwnProperty.call(revealedPasswords, targetId)) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      return;
    }

    setLoadingPasswordIds((prev) => new Set(prev).add(targetId));
    try {
      const response = await revealUserPassword(targetId).unwrap();
      setRevealedPasswords((prev) => ({
        ...prev,
        [targetId]: response?.password || "",
      }));
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reveal password.");
    } finally {
      setLoadingPasswordIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  };

  const copyToClipboard = async (label, value) => {
    const text = String(value || "").trim();
    if (!text || text === "-") {
      showStatus("warning", `${label} not available.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showStatus("success", `${label} copied.`);
    } catch {
      showStatus("error", `Failed to copy ${label.toLowerCase()}.`);
    }
  };

  const renderUserLinkCell = (label, entries, title, canOpenDetails = true) => {
    if (!Array.isArray(entries) || !entries.length || label === "-") {
      return <Typography sx={{ fontWeight: 700 }}>{label}</Typography>;
    }

    if (!canOpenDetails) {
      return <Typography sx={{ fontWeight: 700 }}>{label}</Typography>;
    }

    return (
      <Button
        variant="text"
        onClick={() => handleOpenUserInfoDialog(title, entries)}
        sx={{
          p: 0,
          minWidth: 0,
          textTransform: "none",
          justifyContent: "flex-start",
          fontWeight: 700,
          color: "#2563eb",
          "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "82vh" } }}>
        <DialogContent sx={{ p: 0, bgcolor: "#eef4ff" }}>
          <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2, bgcolor: "#fff" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Devices</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {modalMode === "table" ? (
            <>
              <Box sx={{ px: 3, pt: 1 }}>
                <Tabs value={primaryTab} onChange={(_, value) => { setPrimaryTab(value); setPage(1); }}>
                  {availableTabs.map((tab) => (
                    <Tab key={tab.value} value={tab.value} label={tab.label} />
                  ))}
                </Tabs>
              </Box>

             {primaryTab === "devices" ? (
                <Box sx={{ px: 2, py: 2, borderTop: "1px solid #dbe4f5", borderBottom: "1px solid #dbe4f5", bgcolor: "#eef4ff", display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  {[
                    { value: "all", label: "All Devices", count: summaryCounts.all },
                    { value: "today", label: "Added Today", count: summaryCounts.today },
                    { value: "month", label: "Added Month", count: summaryCounts.month },
                    { value: "expiringToday", label: "Expiring today", count: summaryCounts.expiringToday },
                    { value: "expiringWeek", label: "Expiring in Week", count: summaryCounts.expiringWeek },
                    { value: "expiringMonth", label: "Expiring in Month", count: summaryCounts.expiringMonth },
                  ].map((item) => (
                    <Button
                      key={item.value}
                      onClick={() => { setSummaryTab(item.value); setPage(1); }}
                      variant={summaryTab === item.value ? "contained" : "text"}
                      sx={{
                        borderRadius: 1.5, px: 1.8, py: 1,
                        bgcolor: summaryTab === item.value ? "#fff" : "rgba(255,255,255,0.55)",
                        color: "#1f2937",
                        boxShadow: summaryTab === item.value ? "0 1px 2px rgba(15,23,42,.08)" : "none",
                        textTransform: "none", fontWeight: 800,
                      }}
                    >
                      {item.label}
                      <Box sx={{ ml: 1, px: 0.8, borderRadius: 1, bgcolor: "#6b7280", color: "#fff", fontSize: 12, lineHeight: "22px", minWidth: 24 }}>
                        {item.count}
                      </Box>
                    </Button>
                  ))}
                </Box>
              ) : primaryTab === "renewal" ? (
                <Box sx={{ px: 2, py: 2, borderTop: "1px solid #dbe4f5", borderBottom: "1px solid #dbe4f5", bgcolor: "#eef4ff", display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                  <Typography sx={{ fontWeight: 700, color: "#374151", fontSize: 14 }}>From</Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={renewalFromDate}
                    onChange={(e) => { setRenewalFromDate(e.target.value); setRenewalQuick("Custom Date"); }}
                    sx={{ ...inputSx, width: 150 }}
                    inputProps={{ max: renewalToDate }}
                  />
                  <Select size="small" value={renewalFromHour} onChange={(e) => setRenewalFromHour(Number(e.target.value))} sx={{ ...inputSx, width: 72 }}>
                    {HOUR_OPTIONS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                  </Select>
                  <Select size="small" value={renewalFromMin} onChange={(e) => setRenewalFromMin(Number(e.target.value))} sx={{ ...inputSx, width: 72 }}>
                    {MIN_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>

                  <Typography sx={{ fontWeight: 700, color: "#374151", fontSize: 14 }}>to</Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={renewalToDate}
                    onChange={(e) => { setRenewalToDate(e.target.value); setRenewalQuick("Custom Date"); }}
                    sx={{ ...inputSx, width: 150 }}
                    inputProps={{ min: renewalFromDate }}
                  />
                  <Select size="small" value={renewalToHour} onChange={(e) => setRenewalToHour(Number(e.target.value))} sx={{ ...inputSx, width: 72 }}>
                    {HOUR_OPTIONS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                  </Select>
                  <Select size="small" value={renewalToMin} onChange={(e) => setRenewalToMin(Number(e.target.value))} sx={{ ...inputSx, width: 72 }}>
                    {MIN_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>

                  <Autocomplete
                    size="small"
                    options={vehicleOptions}
                    value={vehicleOptions.find((v) => v.value === renewalVehicleId) || null}
                    inputValue={renewalVehicleInput}
                    onInputChange={(_, val, reason) => {
                      if (reason !== "reset") setRenewalVehicleInput(val);
                    }}
                    onChange={(_, opt) => {
                      setRenewalVehicleId(opt?.value || null);
                      setRenewalVehicleInput(opt?.label || "");
                    }}
                    sx={{ width: 240 }}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    getOptionLabel={(opt) => opt?.label || ""}
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Vehicle"
                        sx={inputSx}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <SearchIcon sx={{ color: "#fb7185", fontSize: 20, mr: 0.5 }} />
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <Select
                    size="small"
                    value={renewalQuick}
                    onChange={(e) => applyRenewalQuick(e.target.value)}
                    sx={{ ...inputSx, width: 140 }}
                  >
                    {["Today", "Week", "Month", "Custom Date"].map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>

                  <Button
                    variant="contained"
                    onClick={handleRenewalSearch}
                    sx={{ bgcolor: "#2563eb", textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleCsvDownload}
                    sx={{ bgcolor: "#16a34a", textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                  >
                    Download
                  </Button>
                </Box>
              ) : primaryTab === "expiring" ? (
                <>
                  <Box sx={{ px: 2, pt: 2, pb: 1, borderTop: "1px solid #dbe4f5", bgcolor: "#eef4ff", display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                    {[
                      { value: "expired", label: "Expired Devices", count: summaryCounts.expiredCount },
                      { value: "7", label: "Expiring in 7 Days", count: summaryCounts.expiring7 },
                      { value: "15", label: "Expiring in 15 Days", count: summaryCounts.expiring15 },
                      { value: "30", label: "Expiring in 30 Days", count: summaryCounts.expiring30 },
                      { value: "60", label: "Expiring in 60 Days", count: summaryCounts.expiring60 },
                    ].map((item) => (
                      <Button
                        key={item.value}
                        onClick={() => { setExpiringCategory(item.value); setPage(1); }}
                        variant={expiringCategory === item.value ? "contained" : "text"}
                        sx={{
                          borderRadius: 1.5, px: 1.8, py: 1,
                          bgcolor: expiringCategory === item.value ? "#fff" : "rgba(255,255,255,0.55)",
                          color: "#1f2937",
                          boxShadow: expiringCategory === item.value ? "0 1px 2px rgba(15,23,42,.08)" : "none",
                          textTransform: "none", fontWeight: 800,
                        }}
                      >
                        {item.label}
                        <Box sx={{ ml: 1, px: 0.8, borderRadius: 1, bgcolor: "#6b7280", color: "#fff", fontSize: 12, lineHeight: "22px", minWidth: 24 }}>
                          {item.count ?? "—"}
                        </Box>
                      </Button>
                    ))}
                  </Box>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #dbe4f5", bgcolor: "#eef4ff", display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                    <Select
                      size="small"
                      value={expiringCategory}
                      onChange={(e) => setExpiringCategory(e.target.value)}
                      sx={{ ...inputSx, width: 190 }}
                    >
                      <MenuItem value="expired">Expired Devices</MenuItem>
                      <MenuItem value="7">Expiring in 7 Days</MenuItem>
                      <MenuItem value="15">Expiring in 15 Days</MenuItem>
                      <MenuItem value="30">Expiring in 30 Days</MenuItem>
                      <MenuItem value="60">Expiring in 60 Days</MenuItem>
                    </Select>

                    <Typography sx={{ fontWeight: 700, color: "#374151", fontSize: 14 }}>From</Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={expiringFromDate}
                      onChange={(e) => { setExpiringFromDate(e.target.value); setExpiringQuick("Custom Date"); }}
                      sx={{ ...inputSx, width: 150 }}
                    />

                    <Typography sx={{ fontWeight: 700, color: "#374151", fontSize: 14 }}>to</Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={expiringToDate}
                      onChange={(e) => { setExpiringToDate(e.target.value); setExpiringQuick("Custom Date"); }}
                      sx={{ ...inputSx, width: 150 }}
                    />

                    <Select
                      size="small"
                      value={expiringQuick}
                      onChange={(e) => applyExpiringQuick(e.target.value)}
                      sx={{ ...inputSx, width: 140 }}
                    >
                      {["Week", "Month", "Custom Date"].map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>

                    <Button
                      variant="contained"
                      onClick={handleExpiringSearch}
                      sx={{ bgcolor: "#2563eb", textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCsvDownload}
                      sx={{ bgcolor: "#16a34a", textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
                    >
                      Download
                    </Button>
                  </Box>
                </>
              ) : primaryTab === "deleted" ? (
                <Box sx={{ px: 2, py: 2, borderTop: "1px solid #dbe4f5", borderBottom: "1px solid #dbe4f5", bgcolor: "#eef4ff", display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  <Button
                    variant="contained"
                    sx={{
                      borderRadius: 1.5, px: 1.8, py: 1,
                      bgcolor: "#fff", color: "#1f2937",
                      boxShadow: "0 1px 2px rgba(15,23,42,.08)",
                      textTransform: "none", fontWeight: 800,
                    }}
                  >
                    Deleted Devices
                    <Box sx={{ ml: 1, px: 0.8, borderRadius: 1, bgcolor: "#6b7280", color: "#fff", fontSize: 12, lineHeight: "22px", minWidth: 24 }}>
                      {summaryCounts.deleted}
                    </Box>
                  </Button>
                </Box>
              ) : null}

              <Box sx={{ px: 2, pt: 2 }}>
                <Paper sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "0 10px 30px rgba(37,99,235,.08)" }}>
                <Box sx={{
                    px: 2, py: 2, borderBottom: "1px solid #e5e7eb",
                    ...(primaryTab === "renewal" || primaryTab === "expiring"
                      ? { display: "flex", justifyContent: "flex-end", alignItems: "center" }
                      : { display: "grid", gridTemplateColumns: { xs: "1fr", md: "220px 1fr auto" }, gap: 1.5, alignItems: "center" }),
                  }}>
                    {primaryTab !== "renewal" && primaryTab !== "expiring" && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography sx={{ minWidth: 88, color: "#374151", fontWeight: 500 }}>Filter By</Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={filterBy}
                            onChange={(event) => {
                              setFilterBy(event.target.value);
                              setSearchQuery("");
                              setFilterInputValue("");
                              setPage(1);
                            }}
                            sx={inputSx}
                          >
                            {FILTER_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                    )}

                    {primaryTab !== "renewal" && primaryTab !== "expiring" && (
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
                        {filterUsesDropdown ? (
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={filterSelectOptions}
                            value={selectedFilterOption}
                            inputValue={filterInputValue}
                            onInputChange={(_, value, reason) => {
                              if (reason === "reset" && selectedFilterOption?.label) {
                                setFilterInputValue(selectedFilterOption.label);
                                return;
                              }
                              setFilterInputValue(value);
                            }}
                            onChange={(_, option) => {
                              setSearchQuery(option?.value || "");
                              setFilterInputValue(option?.label || "");
                              setPage(1);
                            }}
                            isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                            getOptionLabel={(option) => option?.label || ""}
                            filterOptions={(options, state) => {
                              const query = normalizeFilterValue(state.inputValue);
                              if (!query) return options;
                              return options.filter((option) =>
                                normalizeFilterValue(
                                  [option?.label, option?.searchText, option?.value].filter(Boolean).join(" ")
                                ).includes(query)
                              );
                            }}
                            clearOnBlur={false}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Search ${FILTER_OPTIONS.find((option) => option.value === filterBy)?.label || "option"}`}
                                sx={{ ...inputSx, flex: 1 }}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            placeholder="Search"
                            value={searchQuery}
                            onChange={(event) => {
                              setSearchQuery(event.target.value);
                              setPage(1);
                            }}
                            sx={{ ...inputSx, flex: 1 }}
                            InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                          />
                        )}
                      </Stack>
                    )}

                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                      {showSelectionColumn && selectedDeviceIds.length > 0 && (
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleBulkDelete}
                          disabled={bulkDeleting}
                          sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 700 }}
                        >
                          {bulkDeleting ? "Deleting..." : `Delete Selected (${selectedDeviceIds.length})`}
                        </Button>
                      )}
                      {currentUserRole !== ROLES.USER && (
                        <Tooltip title="Add Device">
                          <IconButton onClick={() => { setEditingDevice(null); setModalMode("create"); }} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {currentUserRole !== ROLES.USER && (
                        <Tooltip title="Import Devices">
                          <IconButton onClick={() => fileInputRef.current?.click()} sx={{ color: "#334155" }}>
                            <UploadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Download Devices CSV">
                        <IconButton onClick={handleCsvDownload} sx={{ color: "#16a34a" }}>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Refresh">
                        <IconButton onClick={() => refetch()} sx={{ color: "#6b7280" }}>
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      {currentUserRole !== ROLES.USER && (
                        <Tooltip title="Download Import Template">
                          <IconButton onClick={handleTemplateDownload} sx={{ color: "#6b7280" }}>
                            <PlaylistAddCheckCircleOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={handleImportDevices} />
                    </Stack>
                  </Box>

                  <TableContainer sx={{ maxHeight: "58vh", overflowX: "auto" }}>
                    <Table stickyHeader size="small" sx={{ minWidth: showSelectionColumn ? 2290 : 2230 }}>
                      <TableHead>
                        <TableRow>
                          {showSelectionColumn && (
                            <TableCell sx={{ ...stickySx(0, 60, 6), fontWeight: 700, color: "#6b7280", textAlign: "center" }}>
                              <Checkbox
                                checked={allPageSelected}
                                indeterminate={somePageSelected}
                                onChange={(event) => toggleSelectCurrentPage(event.target.checked)}
                                size="small"
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ ...stickySx(showSelectionColumn ? 60 : 0, 180, 5), fontWeight: 700, color: "#6b7280" }}>Vehicle</TableCell>
                          <TableCell sx={{ ...stickySx(showSelectionColumn ? 240 : 180, 210, 5), fontWeight: 700, color: "#6b7280" }}>IMEI</TableCell>
                          <TableCell sx={{ minWidth: 140, fontWeight: 700, color: "#6b7280" }}>Group Name</TableCell>
                          <TableCell sx={{ minWidth: 140, fontWeight: 700, color: "#6b7280" }}>Status</TableCell>
                          <TableCell sx={{ minWidth: 170, fontWeight: 700, color: "#6b7280" }}>Last Update</TableCell>
                          <TableCell sx={{ minWidth: 140, fontWeight: 700, color: "#6b7280" }}>Dealers</TableCell>
                          <TableCell sx={{ minWidth: 140, fontWeight: 700, color: "#6b7280" }}>Users</TableCell>
                          <TableCell sx={{ minWidth: 180, fontWeight: 700, color: "#6b7280" }}>Created By</TableCell>
                          <TableCell sx={{ minWidth: 140, fontWeight: 700, color: "#6b7280" }}>Sim No</TableCell>
                          <TableCell sx={{ minWidth: 90, fontWeight: 700, color: "#6b7280" }}>Model</TableCell>
                          <TableCell sx={{ minWidth: 110, fontWeight: 700, color: "#6b7280" }}>Contact</TableCell>
                          <TableCell sx={{ minWidth: 90, fontWeight: 700, color: "#6b7280" }}>Icon</TableCell>
                          <TableCell sx={{ minWidth: 90, fontWeight: 700, color: "#6b7280" }}>Disabled</TableCell>
                          <TableCell sx={{ minWidth: 170, fontWeight: 700, color: "#6b7280" }}>License Expiration Timsse</TableCell>
                          <TableCell sx={{ minWidth: 140, fontWeight: 700, color: "#6b7280" }}>Creation Time</TableCell>
                          <TableCell sx={{ minWidth: 110, fontWeight: 700, color: "#6b7280" }}>Todays Odo</TableCell>
                          <TableCell sx={{ minWidth: 110, fontWeight: 700, color: "#6b7280" }}>Total Odo</TableCell>
                          <TableCell sx={{ minWidth: 110, fontWeight: 700, color: "#6b7280" }}>License Type</TableCell>
                          <TableCell sx={{ minWidth: 120, fontWeight: 700, color: "#6b7280" }}>Remarks</TableCell>
                          <TableCell sx={{ minWidth: 100, fontWeight: 700, color: "#6b7280" }}>Integration</TableCell>
                          <TableCell sx={{ minWidth: 220, fontWeight: 700, color: "#6b7280" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedRows.length ? (
                          pagedRows.map((row) => (
                            <TableRow key={row.id} hover>
                              {showSelectionColumn && (
                                <TableCell sx={{ ...stickySx(0, 60, 5), textAlign: "center" }}>
                                  {!row.deleted ? (
                                    <Checkbox
                                      checked={selectedIdSet.has(row.id)}
                                      onChange={() => toggleRowSelection(row.id)}
                                      size="small"
                                    />
                                  ) : null}
                                </TableCell>
                              )}
                              <TableCell sx={{ ...stickySx(showSelectionColumn ? 60 : 0, 180, 4), fontWeight: 700 }}>{row.vehicle}</TableCell>
                              <TableCell sx={{ ...stickySx(showSelectionColumn ? 240 : 180, 210, 4), fontWeight: 700 }}>{row.imei}</TableCell>
                              <TableCell>{row.groupName}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.8} alignItems="center">
                                  <CircleIcon sx={{ fontSize: 12, color: getStatusColor(row.status) }} />
                                  <Typography sx={{ fontWeight: 700 }}>{row.status}</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.lastUpdate}</TableCell>
                              <TableCell>{renderUserLinkCell(row.dealers, row.dealerEntries, "Dealers")}</TableCell>
                              <TableCell>{renderUserLinkCell(row.users, row.userEntries, "Users")}</TableCell>
                              <TableCell>
                                {renderUserLinkCell(
                                  row.createdBy,
                                  row.createdByEntry,
                                  "Created By",
                                  currentUserRole === ROLES.SUPER_ADMIN,
                                )}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.simNo}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.model}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.contact}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.icon}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.disabled}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.licenseExpiryTime}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.creationTime}</TableCell>
                              <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", minWidth: 170 }}>
                                {editingOdo?.deviceId === row.id && editingOdo?.field === "todaysOdo" ? (
                                  <Stack direction="row" spacing={0.75} alignItems="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={editingOdoValue}
                                      onChange={(event) => setEditingOdoValue(event.target.value)}
                                      inputProps={{ min: 0, step: 0.01 }}
                                      sx={{ width: 92, ...inputSx }}
                                    />
                                    <Button size="small" variant="contained" onClick={saveOdoEdit}>
                                      Save
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={cancelOdoEdit}>
                                      Cancel
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography sx={{ fontWeight: 700 }}>{formatOdoCell(row.raw?.todaysOdo ?? row.todaysOdo)}</Typography>
                                    {deviceActions.canEdit && (
                                      <Tooltip title="Edit Today's Odo">
                                        <IconButton size="small" color="primary" onClick={() => beginOdoEdit(row, "todaysOdo")}>
                                          <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap", minWidth: 170 }}>
                                {editingOdo?.deviceId === row.id && editingOdo?.field === "totalOdo" ? (
                                  <Stack direction="row" spacing={0.75} alignItems="center">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={editingOdoValue}
                                      onChange={(event) => setEditingOdoValue(event.target.value)}
                                      inputProps={{ min: 0, step: 0.01 }}
                                      sx={{ width: 92, ...inputSx }}
                                    />
                                    <Button size="small" variant="contained" onClick={saveOdoEdit}>
                                      Save
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={cancelOdoEdit}>
                                      Cancel
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography sx={{ fontWeight: 700 }}>{formatOdoCell(row.raw?.totalOdo ?? row.totalOdo)}</Typography>
                                    {deviceActions.canEdit && (
                                      <Tooltip title="Edit Total Odo">
                                        <IconButton size="small" color="primary" onClick={() => beginOdoEdit(row, "totalOdo")}>
                                          <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.licenseType}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.remarks}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{row.integration}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.8} alignItems="center">
                                  {!row.deleted && deviceActions.canEdit && (
                                    <Tooltip title="Edit Device">
                                      <IconButton
                                        size="small"
                                        onClick={async () => {
                                          if (currentUserRole === ROLES.USER) {
                                            showStatus("error", "Not allowed: User accounts cannot update device details or extend validity.");
                                            return;
                                          }
                                          setEditingDevice(await ensureDeviceDetail(row.raw));
                                          setModalMode("edit");
                                        }}
                                        sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                      >
                                        <EditIcon sx={{ color: "#fff", fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {!row.deleted && (
                                    <Tooltip title="Installation Certificate">
                                      <IconButton size="small" onClick={async () => setCertificateDevice(await ensureDeviceDetail(row.raw))} sx={{ bgcolor: "#2563eb", borderRadius: 1 }}>
                                        <BadgeOutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {!row.deleted && (
                                    <Tooltip title="Assign User">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setAssignUserDevice(row);
                                          setAssignUserId(row.raw?.assignedToUser?._id || row.raw?.assignedToUser || "");
                                        }}
                                        sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                      >
                                        <PersonAddAlt1OutlinedIcon sx={{ color: "#fff", fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {!row.deleted && (
                                    <Tooltip title="Notification Settings">
                                      <IconButton size="small" onClick={async () => setNotificationDevice(await ensureDeviceDetail(row.raw))} sx={{ bgcolor: "#fbbf24", borderRadius: 1 }}>
                                        <NotificationsActiveOutlinedIcon sx={{ color: "#111827", fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="More Actions">
                                    <IconButton size="small" onClick={(event) => openMoreMenu(event, row)} sx={{ color: "#111827" }}>
                                      <MoreHorizIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={showSelectionColumn ? 21 : 20} align="center" sx={{ py: 6, color: "#6b7280" }}>
                              {showCenteredTableLoader ? (
                                <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ minHeight: 180 }}>
                                  <CircularProgress size={36} thickness={4} />
                                  <Typography color="text.secondary">Loading vehicles…</Typography>
                                </Stack>
                              ) : devicesError ? (
                                "Failed to load devices. Please retry."
                              ) : (
                                "No devices found."
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ px: 2.5, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
                    <Typography sx={{ color: "#374151" }}>
                      Showing {displayRange.from} to {displayRange.to} of {effectiveTotalCount} entries
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 88 }}>
                        <Select
                          value={rowsPerPage}
                          onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}
                          MenuProps={ROWS_PER_PAGE_MENU_PROPS}
                        >
                          {CUSTOM_ROWS_PER_PAGE_OPTIONS.map((size) => (
                            <MenuItem key={size} value={size}>
                              {getRowsPerPageLabel(size)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
                    </Stack>
                  </Box>
                </Paper>
              </Box>
            </>
          ) : (
            <Box sx={{ p: 1.5 }}>
              <RegisterDeviceModal
                open
                mode={modalMode === "edit" ? "edit" : "create"}
                initialDevice={editingDevice}
                onClose={closeForm}
                onCreated={async () => {
                  await refetch();
                  closeForm();
                }}
                onUpdated={async () => {
                  await refetch();
                  closeForm();
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          setMenuDevice(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        marginThreshold={16}
        PaperProps={{
          sx: {
            minWidth: 260,
            maxWidth: "min(320px, calc(100vw - 32px))",
            maxHeight: "min(420px, calc(100vh - 96px))",
            overflowY: "auto",
            borderRadius: 2,
            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.18)",
          },
        }}
        MenuListProps={{
          dense: true,
          sx: {
            py: 0.5,
          },
        }}
      >
        {menuDevice?.deleted ? (
          <>
            {deviceActions.canDelete && (
              <MenuItem
                onClick={() => {
                  const current = menuDevice;
                  setMenuAnchorEl(null);
                  if (current) handleRestore(current);
                }}
              >
                <ListItemIcon><RestoreFromTrashOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Restore Device</ListItemText>
              </MenuItem>
            )}
            {deviceActions.canDelete && (
              <MenuItem
                onClick={() => {
                  const current = menuDevice;
                  setMenuAnchorEl(null);
                  if (current) handlePermanentDelete(current);
                }}
              >
                <ListItemIcon><DeleteForeverOutlinedIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Delete Permanently</ListItemText>
              </MenuItem>
            )}
          </>
        ) : (
          <>
            <MenuItem
              onClick={async () => {
                setSensorDevice(menuDevice?.raw ? await ensureDeviceDetail(menuDevice.raw) : null);
                setMenuAnchorEl(null);
              }}
            >
              <ListItemIcon><SensorsOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Assign Sensor</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setCertificateDevice(menuDevice?.raw ? await ensureDeviceDetail(menuDevice.raw) : null);
                setMenuAnchorEl(null);
              }}
            >
              <ListItemIcon><BadgeOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Create Installation Certificate</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAssignUserDevice(menuDevice || null);
                setAssignUserId(menuDevice?.raw?.assignedToUser?._id || menuDevice?.raw?.assignedToUser || "");
                setMenuAnchorEl(null);
              }}
            >
              <ListItemIcon><DirectionsCarFilledOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Assign Service</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAssignGroupDevice(menuDevice || null);
                setMenuAnchorEl(null);
              }}
            >
              <ListItemIcon><GroupWorkOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Assign Group</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={async () => openExtendValidityDialog(menuDevice ? { ...menuDevice, raw: await ensureDeviceDetail(menuDevice.raw) } : null)}
            >
              <ListItemIcon><AutorenewOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Extend Validity</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setReportDevice(menuDevice?.raw ? await ensureDeviceDetail(menuDevice.raw) : null);
                setMenuAnchorEl(null);
              }}
            >
              <ListItemIcon><AssessmentOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Renewal Report</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setReportDevice(menuDevice?.raw ? await ensureDeviceDetail(menuDevice.raw) : null);
                setMenuAnchorEl(null);
              }}
            >
              <ListItemIcon><PlaylistAddCheckCircleOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Audit Data Report</ListItemText>
            </MenuItem>
            {deviceActions.canDelete && (
              <MenuItem
                onClick={() => {
                  const current = menuDevice;
                  setMenuAnchorEl(null);
                  if (current) handleDelete(current);
                }}
              >
                <ListItemIcon><CloseIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Delete Device</ListItemText>
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      <Dialog open={Boolean(assignUserDevice)} onClose={() => { setAssignUserDevice(null); setAssignUserId(""); }} fullWidth maxWidth="sm">
        <DialogTitle>Assign Device User</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Select User"
            value={assignUserId}
            onChange={(event) => setAssignUserId(event.target.value)}
            sx={{ mt: 1 }}
          >
            <MenuItem value="">Unassign</MenuItem>
            {users.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user?.username || user?.name || user?.mobileNo || "User"}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAssignUserDevice(null); setAssignUserId(""); }}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(assignGroupDevice)} onClose={() => { setAssignGroupDevice(null); setAssignGroupId(""); }} fullWidth maxWidth="sm">
        <DialogTitle>Assign Device Group</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Select Group"
            value={assignGroupId}
            onChange={(event) => setAssignGroupId(event.target.value)}
            sx={{ mt: 1 }}
          >
            {groups.map((group) => (
              <MenuItem key={group._id} value={group._id}>
                {group?.name || "Group"}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAssignGroupDevice(null); setAssignGroupId(""); }}>Cancel</Button>
          <Button onClick={handleAssignGroup} variant="contained">Assign</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(extendValidityDevice)} onClose={closeExtendValidityDialog} fullWidth maxWidth="sm">
        <DialogTitle>Extend Validity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Type"
              value="yearly"
              disabled
              sx={inputSx}
            >
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label="Renewal for"
              value={extendValidityYears}
              onChange={(event) => setExtendValidityYears(Number(event.target.value))}
              sx={inputSx}
            >
              {EXTEND_VALIDITY_OPTIONS.map((years) => (
                <MenuItem key={years} value={years}>
                  {years} {years === 1 ? "year" : "years"}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Remarks"
              multiline
              minRows={4}
              value={extendValidityRemark}
              onChange={(event) => setExtendValidityRemark(event.target.value)}
              sx={inputSx}
            />

            <Box>
              <Typography sx={{ fontWeight: 700, color: "#374151", mb: 1 }}>Renew from</Typography>
              <RadioGroup
                row
                value={extendValidityFrom}
                onChange={(event) => setExtendValidityFrom(event.target.value)}
              >
                <FormControlLabel value="original" control={<Radio />} label="Original Date" />
                <FormControlLabel value="current" control={<Radio />} label="Current Date" />
              </RadioGroup>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                Base date: {formatDate(extendValidityBaseDate)}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                New expiry: {formatDate(extendValidityNextExpiry)}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeExtendValidityDialog} disabled={updatingDevice}>Cancel</Button>
          <Button onClick={handleExtendValidity} variant="contained" disabled={updatingDevice}>
            {updatingDevice ? "Saving..." : "Extend Validity"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeviceNotificationSettingsDialog
        open={Boolean(notificationDevice)}
        onClose={() => setNotificationDevice(null)}
        device={notificationDevice}
        showStatus={showStatus}
      />

      <Dialog
        open={Boolean(userInfoDialog)}
        onClose={() => setUserInfoDialog(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
          {userInfoDialog?.title || "User Details"}
          <IconButton onClick={() => setUserInfoDialog(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.5}>
            {userInfoDialog?.entries?.length > 1 ? (
              <TextField
                select
                label="Name"
                value={userInfoDialog?.selectedId || ""}
                onChange={(event) =>
                  setUserInfoDialog((prev) => ({ ...prev, selectedId: event.target.value }))
                }
                fullWidth
                sx={inputSx}
              >
                {userInfoDialog.entries.map((entry) => (
                  <MenuItem key={entry.id || entry.label} value={entry.id}>
                    {entry.summary?.name || entry.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Name"
                value={selectedUserInfoEntry?.summary?.name || selectedUserInfoEntry?.label || "-"}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
            )}

            <Stack direction="row" spacing={1}>
              <TextField
                label="Login Id"
                value={selectedUserInfoEntry?.summary?.uniqueId || "-"}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
              <Tooltip title="Copy Login Id">
                <IconButton
                  onClick={() => copyToClipboard("Login Id", selectedUserInfoEntry?.summary?.uniqueId)}
                  sx={{ alignSelf: "stretch", bgcolor: "#fbbf24", color: "#111827", borderRadius: 1.5, width: 48 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1}>
              <TextField
                label="Password"
                value={
                  selectedUserInfoEntry?.id && Object.prototype.hasOwnProperty.call(revealedPasswords, selectedUserInfoEntry.id)
                    ? revealedPasswords[selectedUserInfoEntry.id]
                    : "••••••••"
                }
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
              <Tooltip
                title={
                  selectedUserInfoEntry?.id && Object.prototype.hasOwnProperty.call(revealedPasswords, selectedUserInfoEntry.id)
                    ? "Hide Password"
                    : "Show Password"
                }
              >
                <IconButton
                  onClick={() => handleTogglePassword(selectedUserInfoEntry?.id)}
                  disabled={!selectedUserInfoEntry?.id || loadingPasswordIds.has(selectedUserInfoEntry?.id)}
                  sx={{ alignSelf: "stretch", bgcolor: "#fbbf24", color: "#111827", borderRadius: 1.5, width: 48 }}
                >
                  {loadingPasswordIds.has(selectedUserInfoEntry?.id) ? (
                    <CircularProgress size={18} sx={{ color: "#111827" }} />
                  ) : selectedUserInfoEntry?.id && Object.prototype.hasOwnProperty.call(revealedPasswords, selectedUserInfoEntry.id) ? (
                    <VisibilityOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy Password">
                <IconButton
                  onClick={() => copyToClipboard("Password", revealedPasswords[selectedUserInfoEntry?.id] || "")}
                  disabled={!selectedUserInfoEntry?.id || !Object.prototype.hasOwnProperty.call(revealedPasswords, selectedUserInfoEntry?.id || "")}
                  sx={{ alignSelf: "stretch", bgcolor: "#fbbf24", color: "#111827", borderRadius: 1.5, width: 48 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1}>
              <TextField
                label="Contact"
                value={selectedUserInfoEntry?.summary?.mobileNo || "-"}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
              <Tooltip title="Copy Contact">
                <IconButton
                  onClick={() => copyToClipboard("Contact", selectedUserInfoEntry?.summary?.mobileNo)}
                  sx={{ alignSelf: "stretch", bgcolor: "#fbbf24", color: "#111827", borderRadius: 1.5, width: 48 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" color="inherit" onClick={() => setUserInfoDialog(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <InstallationCertificateModal
        open={Boolean(certificateDevice)}
        device={certificateDevice}
        onClose={() => setCertificateDevice(null)}
      />

      {sensorDevice ? <SensorAssignModal device={sensorDevice} onClose={() => setSensorDevice(null)} /> : null}

      {reportDevice ? (
        <ReportsPortal
          isOpen
          onClose={() => setReportDevice(null)}
          allDevices={[reportDevice]}
          rawData={[reportDevice]}
        />
      ) : null}
    </>
  );
};

export default DeviceListPortalModal;
